
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  signIn,
  signUp,
  signOut,
  signInWithRedirect,
  getCurrentUser,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword
} from "aws-amplify/auth";
import LogoImg from "../assets/layerbound-white.png";
import GoogleLogo from "../assets/google-logo.svg";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { stripControlCharacters, toPlainString } from "../utils/sanitize";
import { REBRAND_NOTICE } from "../constants/notices";
import "./AuthPage.css";

// Typeform-style typing animation component
function TypingIntro({ lines, speed = 60, pause = 1100 }) {
  const [displayed, setDisplayed] = useState("");
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  useEffect(() => {
    if (lineIdx >= lines.length) return;
    if (charIdx < lines[lineIdx].length) {
      const timeout = setTimeout(() => {
        setDisplayed((prev) => prev + lines[lineIdx][charIdx]);
        setCharIdx((c) => c + 1);
      }, speed);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setDisplayed("");
        setLineIdx((l) => l + 1);
        setCharIdx(0);
      }, pause);
      return () => clearTimeout(timeout);
    }
  }, [charIdx, lineIdx, lines, speed, pause]);
  return (
    <div className="typing-intro">
      <span>{displayed}</span>
      <span className="typing-cursor">|</span>
    </div>
  );
}

const initialForm = {
  email: "",
  password: "",
  name: "",
};

const sanitizePlainText = (value, max = 120) => {
  const normalised = stripControlCharacters(toPlainString(value));
  const cleaned = normalised.replace(/[<>]/g, "");
  return cleaned.slice(0, max);
};

const sanitizeEmailValue = (value) =>
  sanitizePlainText(value, 120).replace(/\s+/g, "").toLowerCase();

const sanitizePasswordValue = (value, max = 64) => {
  const normalised = stripControlCharacters(toPlainString(value), "");
  return normalised.slice(0, max);
};

const sanitizeNumericCode = (value, max = 6) => {
  if (typeof value !== "string") {
    value = value != null ? String(value) : "";
  }
  return value.replace(/\D/g, "").slice(0, max);
};

function AuthPage({ onAuthSuccess }) {
  const [form, setForm] = useState(initialForm);
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [resendCooldown, setResendCooldown] = useState(0);
  const signInAttemptsRef = useRef(0);
  const [signInCooldown, setSignInCooldown] = useState(0);
  const hostedUIConfigured = Boolean(
    process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN &&
      process.env.REACT_APP_OAUTH_REDIRECT_SIGNIN &&
      process.env.REACT_APP_OAUTH_REDIRECT_SIGNOUT
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!resendCooldown) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (!signInCooldown) return undefined;
    const timer = setInterval(() => {
      setSignInCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [signInCooldown]);

  useEffect(() => {
    if (!hostedUIConfigured) return undefined;
    let mounted = true;
    const attemptRestore = async () => {
      try {
        const current = await getCurrentUser();
        if (!mounted || !current?.username) return;
        onAuthSuccess({
          username: current.username,
          email: current?.signInDetails?.loginId || current.username
        });
        navigate("/");
      } catch {
        // ignore when no active OAuth session exists
      }
    };
    attemptRestore();
    return () => {
      mounted = false;
    };
  }, [hostedUIConfigured, navigate, onAuthSuccess]);

  const pageTitle = showForgot
    ? forgotStep === 2
      ? "Reset Password"
      : "Forgot Password"
    : isSignUp
    ? "Create Account"
    : "Sign In";

  usePageMetadata({
    title: pageTitle,
    description:
      "Sign in or create your Layerbound account to manage menus, storefronts, and online orders.",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      let next = value;
      if (name === "email") next = sanitizeEmailValue(value);
      else if (name === "name") next = sanitizePlainText(value, 80);
      else if (name === "password") next = sanitizePasswordValue(value);
      else next = sanitizePlainText(value, 120);
      return { ...prev, [name]: next };
    });
  };

  const ensureSignedOut = async () => {
    try {
      await signOut();
    } catch {}
  };

  const handleGoogleSignIn = async () => {
    if (!hostedUIConfigured) {
      setError("Google sign-in is not configured.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await ensureSignedOut();
      await signInWithRedirect({ provider: "Google" });
    } catch (err) {
      setLoading(false);
      setError(err.message || "Google sign-in failed. Please try again.");
    }
  };

  const handleSignIn = async e => {
    e.preventDefault();
    if (signInCooldown) {
      setError(`Too many attempts. Please wait ${signInCooldown}s before trying again.`);
      return;
    }
    setLoading(true);
    setError("");
    await ensureSignedOut();
    try {
      const { isSignedIn } = await signIn({
        username: form.email,
        password: form.password
      });
      if (isSignedIn) {
        onAuthSuccess({ username: form.email });
        navigate("/"); // Redirect after successful sign-in
        signInAttemptsRef.current = 0;
        setSignInCooldown(0);
      }
    } catch (err) {
      if (err?.name === "UserNotConfirmedException") {
        setShowVerify(true);
        setError("We’ve resent the verification code. Enter it below to activate your account.");
        try {
          await resendSignUpCode({ username: form.email });
          setResendCooldown(60);
        } catch (resendErr) {
          setError(resendErr.message || "Account not verified yet. Use your original code or resend below.");
        }
      } else if (err?.name === "NotAuthorizedException" || err?.name === "UserNotFoundException") {
        const nextAttempts = signInAttemptsRef.current + 1;
        if (nextAttempts >= 5) {
          setSignInCooldown(60);
          signInAttemptsRef.current = 0;
        } else {
          signInAttemptsRef.current = nextAttempts;
        }
        setError(err.message || "Sign in failed");
      } else {
        setError(err.message || "Sign in failed");
      }
    }
    setLoading(false);
  };

  const handleSignUp = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    await ensureSignedOut();
    try {
      const userAttributes = {
        email: form.email,
        name: form.name,
      };

      await signUp({
        username: form.email,
        password: form.password,
        options: {
          userAttributes,
        },
      });
      setShowVerify(true);
      setError("Sign up successful! Please check your email for the verification code.");
    } catch (err) {
      setError(err.message || "Sign up failed");
    }
    setLoading(false);
  };

  const handleVerify = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await confirmSignUp({ username: form.email, confirmationCode: verifyCode });
      setShowVerify(false);
      setIsSignUp(false);
      setError("Verification successful! Please sign in.");
    } catch (err) {
      setError(err.message || "Verification failed");
    }
    setLoading(false);
  };

  const handleResendVerify = async () => {
    if (resendCooldown) return;
    setLoading(true);
    setError("");
    try {
      await resendSignUpCode({ username: form.email });
      setError("Verification code resent. Please check your email.");
      setResendCooldown(60);
    } catch (err) {
      setError(err.message || "Could not resend the code. Please try again shortly.");
    }
    setLoading(false);
  };

  const handleForgotPassword = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await resetPassword({ username: form.email });
      setForgotStep(2);
      setError("A verification code has been sent to your email.");
    } catch (err) {
      setError(err.message || "Failed to send reset code.");
    }
    setLoading(false);
  };
  
  const handleForgotSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await confirmResetPassword({
        username: form.email,
        confirmationCode: forgotCode,
        newPassword: forgotNewPassword
      });
      setShowForgot(false);
      setForgotStep(1);
      setForgotCode("");
      setForgotNewPassword("");
      setError("Password reset successful! Please sign in.");
    } catch (err) {
      setError(err.message || "Password reset failed.");
    }
    setLoading(false);
  };

  const passwordPolicyText =
    "Password must be at least 10 characters and include uppercase, lowercase, a number, and a symbol.";

  // Typeform-style animated intro lines
  const introLines = [
    "Hello 👋",
    "Welcome to Layerbound 🚀",
    "Confirm alignment before the call.",
    "Send a short role-specific link and review responses instantly.",
    "Sign in or create your account to get started."
    
  ];

  const showFullScreenSpinner = loading && !showVerify && !showForgot;

  return (
    <div className="auth-container">
      <nav className="auth-navbar minimalist-navbar">
        <img src={LogoImg} alt="Layerbound" className="nav-logo" />
      </nav>
      <TypingIntro lines={introLines} speed={38} pause={900} />
      <div className="home-title minimalist-title" style={{ marginTop: 18 }}>{isSignUp ? "Create Account" : "Sign In"}</div>
      {showVerify ? (
        <form onSubmit={handleVerify} style={{ width: "100%" }}>
          <input
            className="auth-input"
            type="text"
            name="verifyCode"
            placeholder="Enter verification code"
            value={verifyCode}
            onChange={(e) => setVerifyCode(sanitizeNumericCode(e.target.value))}
            maxLength={6}
            required
          />
          {error && <div className="auth-error">{error}</div>}
          <button className="auth-btn" type="submit" disabled={loading}>
            {loading ? "Please wait..." : "Verify"}
          </button>
          <div style={{ marginTop: 12 }}>
            <button
              className="auth-link"
              type="button"
              onClick={handleResendVerify}
              disabled={loading || resendCooldown > 0}
            >
              {resendCooldown > 0
                ? `Resend available in ${resendCooldown}s`
                : "Didn’t get a code? Resend it"}
            </button>
          </div>
        </form>
      ) : showForgot ? (
        <form onSubmit={forgotStep === 1 ? handleForgotPassword : handleForgotSubmit} style={{ width: "100%" }}>
          <input
            className="auth-input"
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            maxLength={120}
            required
            disabled={forgotStep === 2}
          />
          {forgotStep === 2 && (
            <>
              <input
                className="auth-input"
                type="text"
                name="forgotCode"
                placeholder="Verification code"
                value={forgotCode}
                onChange={(e) => setForgotCode(sanitizeNumericCode(e.target.value))}
                maxLength={6}
                required
              />
              <input
                className="auth-input"
                type="password"
                name="forgotNewPassword"
                placeholder="New password"
                value={forgotNewPassword}
                onChange={(e) => setForgotNewPassword(sanitizePasswordValue(e.target.value))}
                maxLength={64}
                required
              />
            </>
          )}
          {error && <div className="auth-error">{error}</div>}
          <button className="auth-btn" type="submit" disabled={loading}>
            {loading ? "Please wait..." : forgotStep === 1 ? "Send Reset Code" : "Reset Password"}
          </button>
          <button
            className="auth-link"
            type="button"
            onClick={() => {
              setShowForgot(false);
              setForgotStep(1);
              setForgotCode("");
              setForgotNewPassword("");
              setError("");
            }}
            style={{ marginTop: 10 }}
          >
            Back to Sign In
          </button>
        </form>
      ) : (
        <form onSubmit={isSignUp ? handleSignUp : handleSignIn} style={{ width: "100%" }}>
          <div className="auth-rebrand-note">{REBRAND_NOTICE}</div>
          {hostedUIConfigured && (
            <>
              <button
                type="button"
                className="social-btn google-btn"
                onClick={handleGoogleSignIn}
                disabled={loading || signInCooldown > 0}
              >
                <span className="social-btn__icon" aria-hidden="true">
                  <img src={GoogleLogo} alt="" />
                </span>
                Continue with Google
              </button>
              <div className="social-divider">
                <span />
                <span>or continue with email</span>
                <span />
              </div>
            </>
          )}
          <input
            className="auth-input"
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            maxLength={120}
            required
          />
          <input
            className="auth-input"
            type="password"
            name="password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            maxLength={64}
            required
          />
          <div className="auth-password-policy" style={{ fontSize: '0.95em', color: '#6b7280', marginBottom: 8 }}>
            {passwordPolicyText}
          </div>
          {isSignUp && (
            <input
              className="auth-input"
              type="text"
              name="name"
              placeholder="Full Name"
              value={form.name}
              onChange={handleChange}
              maxLength={80}
              required
            />
          )}
          {error && <div className="auth-error">{error}</div>}
          <button className="auth-btn" type="submit" disabled={loading || signInCooldown > 0}>
            {signInCooldown > 0
              ? `Try again in ${signInCooldown}s`
              : loading
              ? "Please wait..."
              : isSignUp
              ? "Sign Up"
              : "Sign In"}
          </button>
          {!isSignUp && (
            <button
              className="auth-link"
              type="button"
              onClick={() => {
                setShowForgot(true);
                setError("");
              }}
              style={{ marginTop: 10 }}
            >
              Forgot password?
            </button>
          )}
        </form>
      )}
      <div className="auth-switch">
        {isSignUp ? (
          <span>
            Already have an account?{" "}
            <button className="auth-link" onClick={() => { setIsSignUp(false); setError(""); }}>Sign In</button>
          </span>
        ) : (
          <span>
            New to Layerbound?{" "}
            <button className="auth-link" onClick={() => { setIsSignUp(true); setError(""); }}>Create Account</button>
          </span>
        )}
      </div>
      <div className="home-footer minimalist-footer">
        <div style={{ marginTop: 8, fontSize: '0.85em', color: '#b0b7c3' }}>© 2025 Layerbound</div>
      </div>
      {showFullScreenSpinner && (
        <div className="auth-loading-overlay" role="status" aria-live="polite">
          <div className="auth-loading-spinner" />
          <div className="auth-loading-label">Signing you in…</div>
        </div>
      )}
    </div>
  );
}

export default AuthPage;
